import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import {
  ArrowLeft,
  ChevronDown,
  CheckCircle2,
  Loader2,
  MinusCircle,
  XCircle,
} from 'lucide-react';

import { courseQueries } from '@/entities/course';
import { useCaptureAnalyticsEvent } from '@/shared/analytics/events';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import { cn } from '@/shared/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/collapsible';
import { PageContainer } from '@/shared/ui/page-container';

const PASSING_GRADE = 70;
const CIRCLE_RADIUS = 72;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

type QuizStatus = 'correct' | 'incorrect' | 'unanswered';

const statusCopy: Record<QuizStatus, { label: string; icon: ReactNode }> = {
  correct: {
    label: 'Correct',
    icon: <CheckCircle2 className="h-5 w-5 text-emerald-400" aria-hidden />,
  },
  incorrect: {
    label: 'Incorrect',
    icon: <XCircle className="h-5 w-5 text-rose-400" aria-hidden />,
  },
  unanswered: {
    label: 'Unanswered',
    icon: <MinusCircle className="h-5 w-5 text-amber-300" aria-hidden />,
  },
};

export const QuizGradingPage = () => {
  const router = useRouter();
  const captureAnalyticsEvent = useCaptureAnalyticsEvent();
  const { courseId, contentId } = useSearch({
    from: '/_user/courses/quiz-grading',
  });

  const { data: quizzes, isPending: isLoadingQuizzes } = useQuery(
    courseQueries.quizzes(contentId ?? ''),
  );

  const quizList = quizzes ?? [];
  const quizResult = (() => {
    const totalQuestions = quizList.length;

    if (!totalQuestions) {
      return {
        totalQuestions: 0,
        correctAnswers: 0,
        incorrectAnswers: 0,
        unanswered: 0,
        score: 0,
        passed: false,
      };
    }

    const correctAnswers = quizList.filter(
      (quiz) => quiz.user_content_quiz_association?.is_answer_correct === true,
    ).length;

    const incorrectAnswers = quizList.filter(
      (quiz) => quiz.user_content_quiz_association?.is_answer_correct === false,
    ).length;

    const unanswered = totalQuestions - (correctAnswers + incorrectAnswers);

    const score = Math.round((correctAnswers / totalQuestions) * 100);

    return {
      totalQuestions,
      correctAnswers,
      incorrectAnswers,
      unanswered,
      score,
      passed: score >= PASSING_GRADE,
    };
  })();

  const questionSummaries = (() => {
    return quizList.map((quiz, index) => {
      const association = quiz.user_content_quiz_association;
      const status: QuizStatus =
        association?.is_answer_correct === true
          ? 'correct'
          : association?.is_answer_correct === false
            ? 'incorrect'
            : 'unanswered';

      return {
        id: quiz.id,
        order: index + 1,
        title: quiz.title ?? quiz.question ?? `Question ${index + 1}`,
        status,
        knowledgeCheckType: quiz.knowledge_check_type,
      };
    });
  })();

  const [isQuestionReviewOpen, setIsQuestionReviewOpen] = useState(true);
  const hasTrackedSummary = useRef(false);

  const isLoading = isLoadingQuizzes;

  const progressPercent = quizResult.totalQuestions
    ? (quizResult.correctAnswers / quizResult.totalQuestions) * 100
    : 0;

  const strokeDashoffset =
    CIRCLE_CIRCUMFERENCE * (1 - progressPercent / 100 || 0);

  useEffect(() => {
    if (hasTrackedSummary.current) return;
    if (!courseId || !contentId) return;
    if (!quizzes) return;

    hasTrackedSummary.current = true;
    captureAnalyticsEvent('quiz_summary_viewed', {
      course_id: courseId,
      content_id: contentId,
      score: quizResult.score,
      passed: quizResult.passed,
      total_questions: quizResult.totalQuestions,
      correct_answers: quizResult.correctAnswers,
      incorrect_answers: quizResult.incorrectAnswers,
      unanswered: quizResult.unanswered,
    });
  }, [
    contentId,
    courseId,
    quizResult.correctAnswers,
    quizResult.incorrectAnswers,
    quizResult.passed,
    quizResult.score,
    quizResult.totalQuestions,
    quizResult.unanswered,
    quizzes,
  ]);

  const handlePrimaryAction = () => {
    if (quizResult.passed) {
      router.navigate({ to: '/certificates' });
      return;
    }

    const firstQuizId = quizzes?.find(
      (quiz) => quiz.user_content_quiz_association?.answer_bool === false,
    )?.id;
    router.navigate({
      to: '/courses/retry-quiz',
      search: {
        courseId,
        contentId,
        quizId: firstQuizId ?? '',
      },
    });
  };

  const handleSecondaryAction = () => {
    router.navigate({ to: '/courses' });
  };

  if (isLoading) {
    return (
      <main className="flex min-h-[calc(100vh-70px)] items-center justify-center bg-slate-950 px-4 text-white">
        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900 px-6 py-4 shadow-[0_20px_50px_rgba(15,23,42,0.45)]">
          <Loader2 className="h-5 w-5 animate-spin text-white/70" />
          <span className="text-sm font-medium tracking-wide">
            Compiling your quiz report...
          </span>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-[calc(100vh-70px)] justify-center bg-slate-950 pt-8 text-white pb-[90px]">
      <PageContainer maxWidth="mobile">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="h-11 w-11 rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/15"
              aria-label="Go Back"
              onClick={() => router.history.back()}
            >
              <ArrowLeft className="h-5 w-5 " />
            </Button>
          </div>

          <section className="overflow-hidden rounded-[32px] border border-white/10 bg-linear-to-br from-slate-900 via-slate-900/90 to-slate-950 shadow-[0_36px_80px_rgba(8,47,73,0.55)]">
            <div className="flex flex-col gap-10 px-6 py-10">
              <div className="flex max-w-xl flex-col gap-6">
                <Badge
                  variant={quizResult.passed ? 'accent' : 'outline'}
                  className={cn('w-fit text-xs uppercase tracking-[0.18em]', {
                    'bg-emerald-500/10 text-emerald-200': quizResult.passed,
                    'text-white/80': !quizResult.passed,
                  })}
                >
                  Performance Report
                </Badge>
                <div>
                  <h2
                    className="text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl"
                    data-testid="quiz-performance-heading"
                  >
                    {quizResult.passed
                      ? 'Congratulations, you passed!'
                      : 'Keep going—you are almost there'}
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-white/70">
                    You answered {quizResult.correctAnswers} out of{' '}
                    {quizResult.totalQuestions} questions correctly. Review the
                    breakdown below for insights and continue when you&apos;re
                    ready.
                  </p>
                </div>
                <div className="flex flex-col gap-3 text-sm text-white/70">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    <span>
                      Passing threshold is {PASSING_GRADE}% accuracy or higher.
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MinusCircle className="h-4 w-4 text-amber-300" />
                    <span>
                      Unanswered questions count against the total score.
                    </span>
                  </div>
                </div>
              </div>

              <div className="relative mx-auto flex h-40 w-40 items-center justify-center">
                <svg
                  className="h-full w-full"
                  viewBox="0 0 180 180"
                  aria-hidden
                >
                  <circle
                    cx="90"
                    cy="90"
                    r={CIRCLE_RADIUS}
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="12"
                    fill="transparent"
                  />
                  <circle
                    cx="90"
                    cy="90"
                    r={CIRCLE_RADIUS}
                    stroke={quizResult.passed ? '#34d399' : '#fb7185'}
                    strokeWidth="12"
                    strokeLinecap="round"
                    fill="transparent"
                    strokeDasharray={CIRCLE_CIRCUMFERENCE}
                    strokeDashoffset={strokeDashoffset}
                    transform="rotate(-90 90 90)"
                    className="transition-[stroke-dashoffset] duration-700 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="text-4xl font-semibold"
                    data-testid="quiz-score-value"
                  >
                    {quizResult.score}
                    <span className="text-xl text-white/40">%</span>
                  </span>
                  <span className="mt-1 text-xs uppercase tracking-[0.25em] text-white/50">
                    Score
                  </span>
                </div>
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.45)]">
            <div>
              <h3 className="text-base font-semibold text-white">
                {quizResult.passed
                  ? 'Great job—your certificate awaits!'
                  : 'Take another attempt to unlock your certificate.'}
              </h3>
              <p className="mt-1.5 text-sm text-white/60">
                {quizResult.passed
                  ? 'View your certificates in the certificates dashboard.'
                  : 'You can return to the quiz and review the questions before submitting again.'}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                variant="secondary"
                className="rounded-2xl border border-white/10 bg-white/10 text-white hover:bg-white/20"
                onClick={handleSecondaryAction}
              >
                Back to Courses
              </Button>
              <Button
                className="rounded-2xl bg-white text-slate-900 hover:bg-slate-200"
                onClick={handlePrimaryAction}
              >
                {quizResult.passed
                  ? 'View Certificates'
                  : 'Retry Incorrect Questions'}
              </Button>
            </div>
          </div>

          <div className="grid gap-4">
            <Card className="border-white/10 bg-white/5 text-white">
              <CardHeader className="p-6 pb-4">
                <CardTitle>Quiz Highlights</CardTitle>
                <CardDescription className="text-white/60">
                  A quick overview of your performance on this knowledge check.
                </CardDescription>
              </CardHeader>
              <CardContent
                className="grid gap-3 p-6 pt-0 grid-cols-2"
                data-testid="quiz-highlights"
              >
                <StatItem label="Score" value={`${quizResult.score}%`} accent />
                <StatItem
                  label="Correct"
                  value={`${quizResult.correctAnswers}`}
                  accent={quizResult.correctAnswers > 0}
                />
                <StatItem
                  label="Incorrect"
                  value={`${quizResult.incorrectAnswers}`}
                  tone="destructive"
                />
                <StatItem
                  label="Unanswered"
                  value={`${quizResult.unanswered}`}
                  tone="warning"
                />
                <StatItem
                  label="Total Questions"
                  value={`${quizResult.totalQuestions}`}
                />
                <StatItem label="Passing Grade" value={`${PASSING_GRADE}%`} />
              </CardContent>
            </Card>

            <Collapsible
              open={isQuestionReviewOpen}
              onOpenChange={setIsQuestionReviewOpen}
            >
              <Card className="border-white/10 bg-white/5 text-white">
                <CardHeader className="p-6 pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-white">
                        Question Review
                      </CardTitle>
                      <CardDescription className="text-white/60">
                        Each question is marked according to your submitted
                        answer.
                      </CardDescription>
                    </div>
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full border border-white/10 bg-white/10 text-white transition hover:bg-white/15"
                        aria-label="Toggle question review"
                        aria-expanded={isQuestionReviewOpen}
                      >
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-300',
                            {
                              '-rotate-180': isQuestionReviewOpen,
                            },
                          )}
                        />
                        <span className="sr-only">Toggle question review</span>
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent
                    className="flex flex-col gap-3 p-6 pt-0"
                    data-testid="quiz-question-review"
                  >
                    {questionSummaries.length ? (
                      questionSummaries.map((question) => {
                        const { icon, label } = statusCopy[question.status];
                        return (
                          <div
                            key={question.id}
                            className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/6 px-4 py-3"
                          >
                            <div>
                              <p className="mt-1 text-sm font-medium text-white">
                                {question.title}
                              </p>
                              {question.knowledgeCheckType ? (
                                <p className="mt-1 text-xs text-white/40">
                                  Type: {question.knowledgeCheckType}
                                </p>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-2 text-sm font-medium text-white/80">
                              {icon}
                              <span>{label}</span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/4 px-6 py-12 text-center text-white/60">
                        <MinusCircle className="h-8 w-8 text-white/30" />
                        <p className="text-sm font-medium">
                          No quiz attempts have been recorded yet.
                        </p>
                        <p className="text-xs text-white/40">
                          Return to the quiz to submit your answers and view the
                          grading summary here.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>
        </div>
      </PageContainer>
    </main>
  );
};

interface StatItemProps {
  label: string;
  value: string;
  accent?: boolean;
  tone?: 'destructive' | 'warning';
}

const slugifyForTestId = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const StatItem = ({ label, value, accent, tone }: StatItemProps) => {
  const toneClasses =
    tone === 'destructive'
      ? 'text-rose-300'
      : tone === 'warning'
        ? 'text-amber-300'
        : accent
          ? 'text-emerald-300'
          : 'text-white';

  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/6 px-4 py-3"
      data-testid={`quiz-stat-${slugifyForTestId(label)}`}
    >
      <p className="text-xs uppercase tracking-[0.18em] text-white/50">
        {label}
      </p>
      <p className={cn('mt-1 text-lg font-semibold', toneClasses)}>{value}</p>
    </div>
  );
};
